var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AuthenticationResult, AuthenticationStatus, ThreeDsStatus } from '@mc-alberta/types';
import getThreeDsBrowserData from '../../methods/getThreeDsBrowserData';
import { Adapter } from '../Adapter';
import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { browserExchange, challenge } from './threeds';
export class ThreeDsAdapter extends Adapter {
    constructor() {
        super(...arguments);
        this.strategy = AuthenticationStrategy.THREE_DS;
        /**
         * Initiates a new 3DS session.
         */
        this.initializeSession = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            this.kpi.start(KpiOperation.INITIATE_AUTHENTICATION);
            this.isIdentityVerificationPerformed = false;
            const threeDsData = {
                threeDsOutputData: this.threeDsOutputData,
                threeDsInputData: Object.assign(Object.assign({}, (_a = this.authenticationContext.dpaTransactionOptions) === null || _a === void 0 ? void 0 : _a.threeDsInputData), { challengeWindowSize: 'FULL', browser: getThreeDsBrowserData(), forceChallenge: this.isChallengeRequired })
            };
            const initialization = yield this.refreshSession({ threeDsData });
            this.methodAttributes = {
                threeDsData: {
                    threeDsOutputData: (_c = (_b = this.authenticationMethod.methodAttributes) === null || _b === void 0 ? void 0 : _b.threeDsData) === null || _c === void 0 ? void 0 : _c.threeDsOutputData
                }
            };
            if (initialization.authenticationStatus !== AuthenticationStatus.PENDING) {
                return this.getSession();
            }
            // the status is pending - proceed with the next step: browser exchange
            threeDsData
                .threeDsInputData
                .browser
                .threeDSCompInd = yield this.getBrowserExchangeResult();
            // browser exchange complete - send that result to auth provider for evaluation
            const session = yield this.refreshSession({ threeDsData });
            if (session.authenticationStatus === AuthenticationStatus.PENDING_CHALLENGE) {
                this.isChallengeRequired = true;
                if (this.suppressChallenge) {
                    // auth provider replied that the user needs to complete a challenge, but merchant forbids it
                    this.authenticationStatus = AuthenticationStatus.COMPLETE;
                }
            }
            return this.getSession();
        });
        /**
         * Injects the 3DS challenge into the DOM element having the given selector.
         *
         * This resolves once the challenge is submitted by the user and validated by ACS.
         */
        this.provideChallenge = (challengeSelector) => __awaiter(this, void 0, void 0, function* () {
            try {
                this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                yield challenge(this.threeDsOutputData, 600000, challengeSelector);
                this.authenticationStatus = AuthenticationStatus.PENDING;
            }
            catch (error) {
                if (error !== '3DS_TIMEOUT') {
                    return this.destroySession(error);
                }
                this.authenticationStatus = AuthenticationStatus.EXPIRED;
            }
            return this.getSession();
        });
        /**
         * Validates the 3DS authentication session.
         */
        this.validateSession = () => __awaiter(this, void 0, void 0, function* () {
            this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            const session = yield this.refreshSession();
            if (session.authenticationResult === AuthenticationResult.AUTHENTICATED) {
                this.isIdentityVerificationPerformed = this.isChallengeRequired;
            }
            else if (session.authenticationStatus === AuthenticationStatus.COMPLETE) {
                this.authenticationStatus = AuthenticationStatus.CHALLENGE_FAILED;
            }
            return this.getSession();
        });
        /**
         * Performs the browser exchange mechanism.
         *
         * @returns the result of the browser exchange process (`Y` = Success, `N` = failure)
         */
        this.getBrowserExchangeResult = () => __awaiter(this, void 0, void 0, function* () {
            try {
                yield browserExchange(this.threeDsOutputData, 10000);
                return ThreeDsStatus.Success;
            }
            catch (error) {
                this.kpi.queueError(error);
            }
            return ThreeDsStatus.Failure;
        });
    }
    /** ACS-specific 3DS data, including browser exchange and challenge data. */
    get threeDsOutputData() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this
            .authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) === null || _b === void 0 ? void 0 : _b.threeDsData) === null || _c === void 0 ? void 0 : _c.threeDsOutputData;
    }
}
//# sourceMappingURL=index.js.map